import {
  baseApi,
  zodPaginatedResponse,
  zodResponse,
  type Paginated,
} from '@saniplus/api-client';
import {
  ExceptionBulkResolveResponseSchema,
  ExceptionDetailSchema,
  type ExceptionListQuerySchema,
  ExceptionSchema,
  ExceptionSummaryResponseSchema,
  type BulkResolveRequest,
  type DismissRequest,
  type Exception,
  type ExceptionBulkResolveResponse,
  type ExceptionDetail,
  type ExceptionSummaryResponse,
  type ResolveRequest,
} from '@saniplus/contracts';
import type { z } from 'zod';

import { toQueryParams } from '../../shared/api/endpoint';

export type ExceptionListArgs = z.input<typeof ExceptionListQuerySchema>;

interface ResolveExceptionArgs {
  readonly id: string;
  readonly body: ResolveRequest;
}

interface DismissExceptionArgs {
  readonly id: string;
  readonly body: DismissRequest;
}

/**
 * The exception queue — everything the night raised that a human has to
 * decide about.
 */
export const exceptionsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listExceptions: build.query<Paginated<Exception>, ExceptionListArgs>({
      query: (args) => ({ url: '/exceptions', params: toQueryParams(args) }),
      ...zodPaginatedResponse(ExceptionSchema),
      providesTags: (result) => [
        { type: 'Exception' as const, id: 'LIST' },
        ...(result?.items ?? []).map((item) => ({ type: 'Exception' as const, id: item.id })),
      ],
    }),

    /** Counts by severity and type for the queue's header; same filters as the list. */
    getExceptionSummary: build.query<ExceptionSummaryResponse, ExceptionListArgs>({
      query: (args) => ({ url: '/exceptions/summary', params: toQueryParams(args) }),
      ...zodResponse(ExceptionSummaryResponseSchema),
      providesTags: [{ type: 'Exception', id: 'LIST' }],
    }),

    getException: build.query<ExceptionDetail, string>({
      query: (id) => ({ url: `/exceptions/${id}` }),
      ...zodResponse(ExceptionDetailSchema),
      providesTags: (_result, _error, id) => [{ type: 'Exception', id }],
    }),

    acknowledgeException: build.mutation<Exception, string>({
      query: (id) => ({ url: `/exceptions/${id}/acknowledge`, method: 'POST', body: {} }),
      ...zodResponse(ExceptionSchema),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Exception', id },
        { type: 'Exception', id: 'LIST' },
      ],
    }),

    resolveException: build.mutation<Exception, ResolveExceptionArgs>({
      query: ({ id, body }) => ({ url: `/exceptions/${id}/resolve`, method: 'POST', body }),
      ...zodResponse(ExceptionSchema),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Exception', id },
        { type: 'Exception', id: 'LIST' },
      ],
    }),

    dismissException: build.mutation<Exception, DismissExceptionArgs>({
      query: ({ id, body }) => ({ url: `/exceptions/${id}/dismiss`, method: 'POST', body }),
      ...zodResponse(ExceptionSchema),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Exception', id },
        { type: 'Exception', id: 'LIST' },
      ],
    }),

    /**
     * A **200 with a partial result**, not an all-or-nothing transaction:
     * `{ resolved, skipped[{ id, reason }] }`. An exception someone else already
     * resolved is reported as skipped rather than failing the whole batch, so
     * the UI must render both halves — treating this as a plain success hides
     * the rows that were not touched.
     *
     * Every submitted id is invalidated, skipped ones included: a skip means the
     * client's copy of that row is the stale one.
     */
    bulkResolveExceptions: build.mutation<ExceptionBulkResolveResponse, BulkResolveRequest>({
      query: (body) => ({ url: '/exceptions/bulk-resolve', method: 'POST', body }),
      ...zodResponse(ExceptionBulkResolveResponseSchema),
      invalidatesTags: (_result, _error, { ids }) => [
        { type: 'Exception' as const, id: 'LIST' },
        ...ids.map((id) => ({ type: 'Exception' as const, id })),
      ],
    }),
  }),
});

export const {
  useListExceptionsQuery,
  useLazyListExceptionsQuery,
  useGetExceptionSummaryQuery,
  useGetExceptionQuery,
  useAcknowledgeExceptionMutation,
  useResolveExceptionMutation,
  useDismissExceptionMutation,
  useBulkResolveExceptionsMutation,
} = exceptionsApi;
